/**
 * Preset Schemas
 *
 * This module contains schemas for different preset types (ACR, ACL Clarke, ACL Competitor).
 * Each preset type has specific configuration and validation rules.
 */

import { z } from 'zod';

import { PresetTypeEnum } from './enums-definitions';
import { ConsumptionInfoSchema } from './consumption-schemas';
import {
  RawEnergyPriceProjectionComponentSchema,
  EnergyPriceInfoDTOSchema,
  MarginFeeSchema,
  GuaranteeTypeSchema,
  PriceListCompositionSchema,
} from './energy-schemas';

// ============================================
// ABSTRACT PRESET BASE
// ============================================
const BooleanPreProcessSchema = (defaultValue: boolean) => {
  return z.preprocess((val) => {
    if (val === false || val === 'false') return false;
    if (val === true || val === 'true') return true;
    return defaultValue;
  }, z.boolean().default(defaultValue).nullish());
};
export const AbstractPresetPayloadSchema = z.object({
  aux__useExpiredDaysProjection: BooleanPreProcessSchema(false),
  nickname: z.string().nullish(),
  tariffGroup: z
    .enum(['A4_GREEN', 'A4_BLUE', 'AS_BLUE', 'AS_GREEN', 'A3_BLUE', 'A2_BLUE', 'B3_STANDARD', 'B3_OPTING'], {
      errorMap: () => ({ message: 'Campo é obrigatório' }),
    })
    .nullish(),
  powerGeneratorCost: z.number().nullish(),
  edc: z.union([z.number(), z.string()]).nullish(),
  icmsType: z.union([z.number(), z.string()]).nullish(),
  companyType: z
    .enum(['FACTORY', 'GAS_STATION', 'RESTAURANT', 'OTHER', 'MARBLE_FACTORY', 'HOSPITAL'], {
      errorMap: () => ({ message: 'Campo é obrigatório' }),
    })
    .nullish(),
  presetType: PresetTypeEnum.nullish(),
  state: z.string().nullish(),
  isAlreadyAtAclBeforeNewRule: z.preprocess((val) => {
    if (val === true || val === 'true') return true;
    if (val === false || val === 'false') return false;
    return null;
  }, z.boolean().nullish()),
  aux__useManagementTerminationFine: BooleanPreProcessSchema(true),
  aux__useEnergyTerminationFine: BooleanPreProcessSchema(true),
  aux__useOpenInWeekends: BooleanPreProcessSchema(false),
  aux__useScenarioSelector: PresetTypeEnum.nullish(),
  aux__useManualMigrationCost: BooleanPreProcessSchema(false),
  aux__useEditableSection_AboutDistribution: BooleanPreProcessSchema(false),
  aux__useEditableSection_ConsumptionHistory: BooleanPreProcessSchema(false),
  aux__useEditableSection_DemandHistory: BooleanPreProcessSchema(false),
  aux__useEditableSection_PeakConsumptionHistory: BooleanPreProcessSchema(false),
  aux__useEditableSection_OffPeakConsumptionHistory: BooleanPreProcessSchema(false),
  aux__useEditableSection_PeakDemandHistory: BooleanPreProcessSchema(false),
  aux__useEditableSection_OffPeakDemandHistory: BooleanPreProcessSchema(false),
  aux__useEditableSection_ContractedDemand: BooleanPreProcessSchema(false),
  aux__useEditableSection_OperatingHours: BooleanPreProcessSchema(false),
  aux__useCustomHistory_ConsumptionHistory: BooleanPreProcessSchema(false),
  aux__useCustomHistory_PeakConsumptionHistory: BooleanPreProcessSchema(false),
  aux__useCustomHistory_OffPeakConsumptionHistory: BooleanPreProcessSchema(false),
  aux__useManualPrices: BooleanPreProcessSchema(false),
  aux__useCustomMargin: BooleanPreProcessSchema(false),
  aux__useSameMarketForAll: BooleanPreProcessSchema(false),
});

// ============================================
// ACL ABSTRACT PRESET
// ============================================

export const AclAbstractPresetPayloadSchema = z.object({
  consumptionInfo: ConsumptionInfoSchema.nullish(),
  smfAdequacy: z.number().nullish(),
  upperFlexibility: z.number().nullish(),
  lowerFlexibility: z.number().nullish(),
  guaranteeType: GuaranteeTypeSchema.nullish(),
  managementPrice: z.number().nullish(),
  aux__useAutoManagementPrice: BooleanPreProcessSchema(false),
  aux__useAutoMigrationCost: BooleanPreProcessSchema(false),
  useIRecCertification: BooleanPreProcessSchema(false),
  aux__useCustomMargin: BooleanPreProcessSchema(false),
  aux__useSameMarketForAll: BooleanPreProcessSchema(false),
  energyType: z
    .enum(['INCENTIVIZED_50', 'INCENTIVIZED_100', 'CONVENTIONAL'], {
      errorMap: () => ({ message: 'Campo é obrigatório' }),
    })
    .nullish(),
  managementTerminationFine: z
    .enum(
      [
        'THIRTY_PERCENT_OF_THE_CONTRACT_RESIDUAL',
        'FIFTY_PERCENT_OF_THE_CONTRACT_RESIDUAL',
        'SEVENTY_PERCENT_OF_THE_CONTRACT_RESIDUAL',
        'ONE_HUNDRED_PERCENT_OF_THE_CONTRACT_RESIDUAL',
        'HAS_A_FINE',
        'NO_FINE',
      ],
      {
        errorMap: () => ({ message: 'Campo é obrigatório' }),
      },
    )
    .nullish(),
  energyTerminationFine: z
    .enum(
      [
        'THIRTY_PERCENT_OF_THE_CONTRACT_RESIDUAL',
        'FIFTY_PERCENT_OF_THE_CONTRACT_RESIDUAL',
        'SEVENTY_PERCENT_OF_THE_CONTRACT_RESIDUAL',
        'ONE_HUNDRED_PERCENT_OF_THE_CONTRACT_RESIDUAL',
        'HAS_A_FINE',
        'NO_FINE',
      ],
      {
        errorMap: () => ({ message: 'Campo é obrigatório' }),
      },
    )
    .nullish(),
  energyPriceInfo: EnergyPriceInfoDTOSchema.nullish(),
});

// ============================================
// ACR PRESET
// ============================================

export const ACRPresetPayloadSchema = AbstractPresetPayloadSchema.extend({
  consumptionInfo: ConsumptionInfoSchema.nullish(),
});

// ============================================
// ACL CLARKE PRESET
// ============================================

export const ACLClarkePresetPayloadSchema = AbstractPresetPayloadSchema.extend({
  productType: z
    .enum(['ACL_WHOLESALER', 'ACL_RETAILER', 'PRODUCT_2'], {
      errorMap: () => ({ message: 'Campo é obrigatório' }),
    })
    .nullish(),
  aux__useProductType: z.enum(['PRODUCT_ONE', 'PRODUCT_TWO']).nullish(),
  modalityType: z
    .enum(['WHOLESALER', 'RETAILER'], {
      errorMap: () => ({ message: 'Campo é obrigatório' }),
    })
    .nullish(),
  rawEnergyPrice: RawEnergyPriceProjectionComponentSchema.nullish(),
  energyPriceInfo: EnergyPriceInfoDTOSchema.nullish(),
  marginFee: MarginFeeSchema.nullish(),
  risk: z
    .enum(['CONSERVATIVE', 'BOLD'], {
      errorMap: () => ({ message: 'Campo é obrigatório' }),
    })
    .nullish(),
  initialExpenses: z.number().nullish(),
  migrationCost: z.number().nullish(),
  clarkeFee: z.number().nullish(),
  isAlreadyAtAclBeforeNewRule: z.boolean().nullish(),
  priceListComposition: PriceListCompositionSchema,
}).merge(AclAbstractPresetPayloadSchema);

// ============================================
// ACL COMPETITOR PRESET
// ============================================

export const ACLCompetitorPresetPayloadSchema = AbstractPresetPayloadSchema.extend({
  competitorId: z.string().nullish(),
  competitorName: z.string().nullish(),
  finalEnergyPrice: z.record(z.any(), z.number().nullish()).nullish(),
  initialExpenses: z.number().nullish(),
  migrationCost: z.number().nullish(),
  managerType: z
    .enum(['INDEPENDENT_MANAGER', 'SALES_MANAGER'], {
      errorMap: () => ({ message: 'Campo é obrigatório' }),
    })
    .nullish(),
  priceListComposition: PriceListCompositionSchema,
  aux__useManualPrices: BooleanPreProcessSchema(false),
  aux__useCustomMargin: BooleanPreProcessSchema(false),
  aux__useSameMarketForAll: BooleanPreProcessSchema(false),
  consumptionInfo: ConsumptionInfoSchema.nullish(),
  smfAdequacy: z.number().nullish(),
  upperFlexibility: z.number().nullish(),
  lowerFlexibility: z.number().nullish(),
  guaranteeType: GuaranteeTypeSchema.nullish(),
  managementPrice: z.number().nullish(),
  aux__useAutoManagementPrice: BooleanPreProcessSchema(false),
  aux__useAutoMigrationCost: BooleanPreProcessSchema(false),
  useIRecCertification: BooleanPreProcessSchema(false),
  modalityType: z
    .enum(['WHOLESALER', 'RETAILER'], {
      errorMap: () => ({ message: 'Campo é obrigatório' }),
    })
    .nullish(),
  energyType: z
    .enum(['INCENTIVIZED_50', 'INCENTIVIZED_100', 'CONVENTIONAL'], {
      errorMap: () => ({ message: 'Campo é obrigatório' }),
    })
    .nullish(),
  managementTerminationFine: z
    .enum(
      [
        'THIRTY_PERCENT_OF_THE_CONTRACT_RESIDUAL',
        'FIFTY_PERCENT_OF_THE_CONTRACT_RESIDUAL',
        'SEVENTY_PERCENT_OF_THE_CONTRACT_RESIDUAL',
        'ONE_HUNDRED_PERCENT_OF_THE_CONTRACT_RESIDUAL',
        'HAS_A_FINE',
        'NO_FINE',
      ],
      {
        errorMap: () => ({ message: 'Campo é obrigatório' }),
      },
    )
    .nullish(),
  energyTerminationFine: z
    .enum(
      [
        'THIRTY_PERCENT_OF_THE_CONTRACT_RESIDUAL',
        'FIFTY_PERCENT_OF_THE_CONTRACT_RESIDUAL',
        'SEVENTY_PERCENT_OF_THE_CONTRACT_RESIDUAL',
        'ONE_HUNDRED_PERCENT_OF_THE_CONTRACT_RESIDUAL',
        'HAS_A_FINE',
        'NO_FINE',
      ],
      {
        errorMap: () => ({ message: 'Campo é obrigatório' }),
      },
    )
    .nullish(),
  energyPriceInfo: EnergyPriceInfoDTOSchema.transform((data) => ({
    ...data,
    origin: 'Manual' as const,
  })).nullish(),
});

// ============================================
// COMBINED PRESET SCHEMAS
// ============================================

export const PresetPayloadSchema = ACRPresetPayloadSchema.merge(ACLCompetitorPresetPayloadSchema).merge(
  ACLClarkePresetPayloadSchema,
);

export const SimulationUnitPayloadSchema = z.object({
  id: z.string().nullish(),
  nickname: z.string().nullish(),
  supplyStartDate: z.union([z.string().nullish(), z.date().nullish()]).nullish(),
  supplyFinalDate: z.union([z.string().nullish(), z.date().nullish()]).nullish(),
});

export const UnitPresetPayloadSchema = z.object({
  id: z.string().uuid().nullish(),
  presetType: PresetTypeEnum.nullish(),
  presetPayload: PresetPayloadSchema.nullish(),
  unitPayload: SimulationUnitPayloadSchema.nullish(),
});
